import { supabase } from '../lib/supabase.js';

/**
 * Service for handling assessment data operations
 * Supports the three assessment modes: regenerate, retake, revise
 */
class AssessmentDataService {
  /**
   * Get the most recent assessment responses for a user
   * Used for revision mode pre-population
   */
  static async getLatestAssessmentResponses(userId) {
    try {
      const { data, error } = await supabase
        .from('assessment_responses')
        .select('id, responses, created_at, assessment_version')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No rows found
          return null;
        }
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error fetching latest assessment:', error);
      throw error;
    }
  }

  /**
   * Get specific assessment by ID
   */
  static async getAssessmentById(assessmentId) {
    try {
      const { data, error } = await supabase
        .from('assessment_responses')
        .select('*')
        .eq('id', assessmentId)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching assessment by ID:', error);
      throw error;
    }
  }

  /**
   * Save a new assessment (fresh retake)
   */
  static async saveNewAssessment(userId, responses, additionalData = {}) {
    try {
      const assessmentData = {
        user_id: userId,
        responses: responses,
        created_at: new Date().toISOString(),
        assessment_version: 'A', // Default to Version A for new assessments
        ...additionalData
      };

      const { data, error } = await supabase
        .from('assessment_responses')
        .insert([assessmentData])
        .select()
        .single();

      if (error) throw error;
      
      console.log('✅ New assessment saved:', data.id);
      return data;
    } catch (error) {
      console.error('Error saving new assessment:', error);
      throw error;
    }
  }

  /**
   * Save a revised assessment (edited answers)
   * Creates new row with revised_from pointing to original
   */
  static async saveRevisedAssessment(userId, responses, originalAssessmentId, additionalData = {}) {
    try {
      const assessmentData = {
        user_id: userId,
        responses: responses,
        revised_from: originalAssessmentId,
        revised_at: new Date().toISOString(),
        created_at: new Date().toISOString(),
        assessment_version: 'A', // New assessments start as Version A
        ...additionalData
      };

      const { data, error } = await supabase
        .from('assessment_responses')
        .insert([assessmentData])
        .select()
        .single();

      if (error) throw error;
      
      console.log('✅ Revised assessment saved:', data.id, 'from original:', originalAssessmentId);
      return data;
    } catch (error) {
      console.error('Error saving revised assessment:', error);
      throw error;
    }
  }

  /**
   * Check if user has any existing assessments
   */
  static async hasExistingAssessments(userId) {
    try {
      const { data, error } = await supabase
        .from('assessment_responses')
        .select('id')
        .eq('user_id', userId)
        .limit(1);

      if (error) throw error;
      return data && data.length > 0;
    } catch (error) {
      console.error('Error checking existing assessments:', error);
      return false;
    }
  }

  /**
   * Get assessment history for a user (for debugging/admin purposes)
   */
  static async getAssessmentHistory(userId) {
    try {
      const { data, error } = await supabase
        .from('assessment_responses')
        .select(`
          id, 
          created_at, 
          assessment_version,
          reprocessed_from,
          reprocessed_at,
          revised_from,
          revised_at,
          responses
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      return data.map(assessment => ({
        ...assessment,
        type: assessment.reprocessed_from ? 'regenerated' : 
              assessment.revised_from ? 'revised' : 'original'
      }));
    } catch (error) {
      console.error('Error fetching assessment history:', error);
      throw error;
    }
  }

  /**
   * Validate assessment responses format
   */
  static validateAssessmentResponses(responses) {
    if (!responses || typeof responses !== 'object') {
      return false;
    }

    // Check for basic assessment structure
    // Adjust these validation rules based on your assessment format
    const requiredFields = ['1', '2', '3', '4', '5', '6', '7', '8'];
    
    for (const field of requiredFields) {
      if (!responses.hasOwnProperty(field)) {
        console.warn('Missing assessment response field:', field);
        return false;
      }
    }

    return true;
  }

  /**
   * Prepare responses for revision mode
   * Ensures compatibility with form pre-population
   */
  static prepareResponsesForRevision(responses) {
    if (!responses) return {};
    
    // Ensure all response fields are strings for form compatibility
    const preparedResponses = {};
    
    for (const [key, value] of Object.entries(responses)) {
      preparedResponses[key] = value ? String(value) : '';
    }
    
    return preparedResponses;
  }
}

export default AssessmentDataService;